/**
 * Retry helper for Supabase REST reads that can intermittently return 503
 * (pooler saturation / statement timeout) before we surface an error state.
 *
 * Usage:
 *   const { data, error } = await retryQuery(() =>
 *     supabase.from('toss_score_history').select('*').eq('token_symbol', sym)
 *   );
 */
const TRANSIENT_PATTERN = /50\d|timeout|timed out|network|fetch|temporarily/i;

export function isTransientError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { code?: string; message?: string; status?: number };
  if (typeof e.status === 'number' && e.status >= 500) return true;
  if (e.code && ['503', '504', '57014', '08006', '08003'].includes(String(e.code))) return true;
  return TRANSIENT_PATTERN.test(e.message || '');
}

export async function retryQuery<T extends { error: unknown }>(
  run: () => PromiseLike<T>,
  { attempts = 3, baseDelayMs = 400, label = 'supabase-query' } = {},
): Promise<T> {
  let result = await run();

  for (let attempt = 1; attempt < attempts; attempt++) {
    if (!isTransientError(result.error)) return result;
    console.warn(`[${label}] transient error, retry ${attempt}/${attempts - 1}`, result.error);
    await new Promise((r) => setTimeout(r, baseDelayMs * Math.pow(2, attempt - 1)));
    result = await run();
  }

  if (result.error && isTransientError(result.error)) {
    console.error(`[${label}] still failing after ${attempts} attempts`, result.error);
  }
  return result;
}
